import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import type {
	TContentPart,
	TPaulInputSubmission,
} from "../../types/agent";

import {
	formatPaulInputResponse,
	getPaulOptionDisplayLabel,
	parsePaulInputSpec,
} from "../../utils/paulInput";

type TPaulInputPromptProps = {
	id?: string;
	part: TContentPart;
	active: boolean;
	answeredValue?: string;
	disabled?: boolean;
	readOnly?: boolean;
	multiSelectMenuMode?: "inline" | "adaptive";
	onSubmit?: (
		submission: TPaulInputSubmission
	) => Promise<void> | void;
};

const OPTION_SEARCH_THRESHOLD = 4;

const getSelectionHint = (
	minSelections: number,
	maxSelections: number
) => {
	if (maxSelections === 1) {
		return minSelections === 0
			? "Choose up to one option"
			: "Choose one option";
	}
	if (minSelections === maxSelections) {
		return `Choose ${minSelections} options`;
	}
	return `Choose ${minSelections}–${maxSelections} options`;
};

const getNumberHint = ({
	min,
	max,
	decimal_digits: decimalDigits,
}: {
	min?: number | null;
	max?: number | null;
	decimal_digits?: number | null;
}) => {
	const hints: Array<string> = [];
	if (min !== undefined && min !== null) {
		hints.push(`minimum ${min}`);
	}
	if (max !== undefined && max !== null) {
		hints.push(`maximum ${max}`);
	}
	if (
		decimalDigits !== undefined &&
		decimalDigits !== null
	) {
		hints.push(`up to ${decimalDigits} decimal places`);
	}
	return hints.join(" · ");
};

const validateNumber = (
	rawValue: string,
	constraints: {
		min?: number | null;
		max?: number | null;
		decimal_digits?: number | null;
	}
): { value: number | null; error: string | null } => {
	const trimmedValue = rawValue.trim();
	if (trimmedValue.length === 0) {
		return { value: null, error: "Enter a number." };
	}

	const value = Number(trimmedValue);
	if (!Number.isFinite(value)) {
		return { value: null, error: "Enter a valid number." };
	}
	if (
		constraints.min !== undefined &&
		constraints.min !== null &&
		value < constraints.min
	) {
		return {
			value: null,
			error: `Enter a value of at least ${constraints.min}.`,
		};
	}
	if (
		constraints.max !== undefined &&
		constraints.max !== null &&
		value > constraints.max
	) {
		return {
			value: null,
			error: `Enter a value no greater than ${constraints.max}.`,
		};
	}
	if (
		constraints.decimal_digits !== undefined &&
		constraints.decimal_digits !== null &&
		Math.abs(
			value -
				Number(value.toFixed(constraints.decimal_digits))
		) > 1e-9
	) {
		return {
			value: null,
			error: `Use no more than ${constraints.decimal_digits} decimal places.`,
		};
	}

	return { value, error: null };
};

const PaulInputPrompt = ({
	id,
	part,
	active,
	answeredValue,
	disabled = false,
	readOnly = false,
	multiSelectMenuMode = "inline",
	onSubmit,
}: TPaulInputPromptProps) => {
	const spec = useMemo(
		() => parsePaulInputSpec(part),
		[part]
	);
	const [selected, setSelected] = useState<Array<string>>(
		[]
	);
	const [isMultiSelectOpen, setIsMultiSelectOpen] =
		useState(false);
	const [multiSelectMenuLayout, setMultiSelectMenuLayout] =
		useState({
			placement: "down" as "down" | "up",
			optionsMaxHeight: 180,
		});
	const [optionSearch, setOptionSearch] = useState("");
	const [value, setValue] = useState("");
	const [validationError, setValidationError] = useState<
		string | null
	>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const multiSelectRef = useRef<HTMLDivElement | null>(
		null
	);
	const multiSelectToggleRef =
		useRef<HTMLButtonElement | null>(null);
	const controlsDisabled = [
		disabled,
		readOnly,
		!active,
		isSubmitting,
	].some(Boolean);
	const promptDisplayContent =
		spec?.content ?? part.content;
	const filteredOptions = useMemo(() => {
		if (!spec || spec.inputKind !== "options") {
			return [];
		}

		const normalizedSearch = optionSearch
			.trim()
			.toLowerCase();
		if (!normalizedSearch) {
			return spec.options;
		}

		return spec.options.filter((option) =>
			`${option.label} ${option.id}`
				.toLowerCase()
				.includes(normalizedSearch)
		);
	}, [optionSearch, spec]);

	const updateMultiSelectMenuLayout = useCallback(() => {
		if (multiSelectMenuMode !== "adaptive") {
			return;
		}

		const multiSelect = multiSelectRef.current;
		const toggle = multiSelectToggleRef.current;
		if (!multiSelect || !toggle) {
			return;
		}

		const scrollBoundary = multiSelect.closest<HTMLElement>(
			".ledger-paul-messages"
		);
		const boundaryRect = scrollBoundary
			? scrollBoundary.getBoundingClientRect()
			: {
					top: 0,
					bottom: window.innerHeight,
				};
		const toggleRect = toggle.getBoundingClientRect();
		const menuGap = 8;
		const availableAbove = Math.max(
			0,
			toggleRect.top - boundaryRect.top - menuGap
		);
		const availableBelow = Math.max(
			0,
			boundaryRect.bottom - toggleRect.bottom - menuGap
		);
		const estimatedOptionsHeight = Math.min(
			220,
			Math.max(40, filteredOptions.length * 34)
		);
		const estimatedMenuHeight = estimatedOptionsHeight + 62;
		const placement =
			availableBelow >= estimatedMenuHeight ||
			availableBelow >= availableAbove
				? "down"
				: "up";
		const availableMenuHeight =
			placement === "down"
				? availableBelow
				: availableAbove;
		const optionsMaxHeight = Math.max(
			72,
			Math.min(220, availableMenuHeight - 62)
		);

		setMultiSelectMenuLayout((currentLayout) =>
			currentLayout.placement === placement &&
			currentLayout.optionsMaxHeight === optionsMaxHeight
				? currentLayout
				: { placement, optionsMaxHeight }
		);
	}, [filteredOptions.length, multiSelectMenuMode]);

	useEffect(() => {
		if (!isMultiSelectOpen) {
			return;
		}

		updateMultiSelectMenuLayout();
		const scrollBoundary =
			multiSelectRef.current?.closest<HTMLElement>(
				".ledger-paul-messages"
			);
		const handleViewportChange = () => {
			updateMultiSelectMenuLayout();
		};

		window.addEventListener("resize", handleViewportChange);
		scrollBoundary?.addEventListener(
			"scroll",
			handleViewportChange
		);
		return () => {
			window.removeEventListener(
				"resize",
				handleViewportChange
			);
			scrollBoundary?.removeEventListener(
				"scroll",
				handleViewportChange
			);
		};
	}, [isMultiSelectOpen, updateMultiSelectMenuLayout]);

	useEffect(() => {
		if (!isMultiSelectOpen) {
			return;
		}

		const handleDocumentInteraction = (event: Event) => {
			if (
				!multiSelectRef.current?.contains(
					event.target as Node
				)
			) {
				setIsMultiSelectOpen(false);
			}
		};

		document.addEventListener(
			"pointerdown",
			handleDocumentInteraction
		);
		document.addEventListener(
			"mousedown",
			handleDocumentInteraction
		);
		document.addEventListener(
			"touchstart",
			handleDocumentInteraction
		);
		return () => {
			document.removeEventListener(
				"pointerdown",
				handleDocumentInteraction
			);
			document.removeEventListener(
				"mousedown",
				handleDocumentInteraction
			);
			document.removeEventListener(
				"touchstart",
				handleDocumentInteraction
			);
		};
	}, [isMultiSelectOpen]);

	useEffect(() => {
		if (controlsDisabled) {
			setIsMultiSelectOpen(false);
		}
	}, [controlsDisabled]);

	if (!spec) {
		if (
			!active &&
			part.msg_type === "input" &&
			part.input_kind === "options" &&
			part.options_source === "dynamic"
		) {
			return (
				<div className="paul-input-prompt is-inactive">
					<div className="paul-input-prompt__header">
						<p>{promptDisplayContent}</p>
						<span>
							{readOnly ? "Read-only" : "Answered"}
						</span>
					</div>
					{answeredValue !== undefined ? (
						<p className="paul-input-prompt__answer">
							{answeredValue}
						</p>
					) : (
						<small>
							The available options were specific to this
							point in the conversation.
						</small>
					)}
				</div>
			);
		}

		return (
			<div className="paul-input-prompt paul-input-prompt--error">
				<p>{part.content}</p>
				<span>
					Paul returned an input that could not be
					displayed. Ask Paul to repeat the question.
				</span>
			</div>
		);
	}

	// Answered pickers render read-only text of the choice, not the (re-)interactive form.
	// Gate on presence, not truthiness: a valid empty answer is still answered.
	if (!active && answeredValue !== undefined) {
		return (
			<div className="paul-input-prompt is-inactive">
				<div className="paul-input-prompt__header">
					<p>{promptDisplayContent}</p>
					<span>{readOnly ? "Read-only" : "Answered"}</span>
				</div>
				<p className="paul-input-prompt__answer">
					{answeredValue}
				</p>
			</div>
		);
	}

	const handleSubmit = async () => {
		if (controlsDisabled || onSubmit === undefined) {
			return;
		}

		let response;
		if (spec.inputKind === "options") {
			if (selected.length < spec.minSelections) {
				setValidationError(
					`Select at least ${spec.minSelections} option${
						spec.minSelections === 1 ? "" : "s"
					}.`
				);
				return;
			}
			if (selected.length > spec.maxSelections) {
				setValidationError(
					`Select no more than ${spec.maxSelections} options.`
				);
				return;
			}
			response = {
				question_id: spec.questionId,
				input_kind: "options" as const,
				selected,
			};
		} else if (spec.answerType === "float") {
			const numberResult = validateNumber(
				value,
				spec.constraints
			);
			if (
				numberResult.error !== null ||
				numberResult.value === null
			) {
				setValidationError(numberResult.error);
				return;
			}
			response = {
				question_id: spec.questionId,
				input_kind: "freeform" as const,
				answer_type: "float" as const,
				value: numberResult.value,
			};
		} else {
			response = {
				question_id: spec.questionId,
				input_kind: "freeform" as const,
				answer_type: "text" as const,
				value,
			};
		}

		setValidationError(null);
		setIsSubmitting(true);
		try {
			await onSubmit({
				response,
				displayValue: formatPaulInputResponse(
					spec,
					response
				),
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	const inactiveLabel = readOnly
		? "Read-only"
		: !active
			? "Answered"
			: null;

	return (
		<div
			id={id}
			className={`paul-input-prompt ${
				active ? "is-active" : "is-inactive"
			}`}
		>
			<div className="paul-input-prompt__header">
				<p>{promptDisplayContent}</p>
				{inactiveLabel ? (
					<span>{inactiveLabel}</span>
				) : null}
			</div>

			{spec.inputKind === "options" ? (
				<fieldset disabled={controlsDisabled}>
					<legend>
						{getSelectionHint(
							spec.minSelections,
							spec.maxSelections
						)}
					</legend>
					{spec.maxSelections > 1 ? (
						<div
							ref={multiSelectRef}
							className={`paul-input-prompt__multi-select ${
								multiSelectMenuMode === "adaptive"
									? "is-adaptive"
									: ""
							} ${isMultiSelectOpen ? "is-open" : ""}`}
						>
							<button
								ref={multiSelectToggleRef}
								type="button"
								className="paul-input-prompt__multi-select-toggle"
								onClick={() => {
									if (!isMultiSelectOpen) {
										updateMultiSelectMenuLayout();
									}
									setIsMultiSelectOpen(
										(current) => !current
									);
								}}
								disabled={controlsDisabled}
								aria-haspopup="listbox"
								aria-expanded={isMultiSelectOpen}
								aria-controls={`paul-options-${spec.questionId}`}
							>
								<span>
									{selected.length > 0
										? `${selected.length} selected`
										: "Select options"}
								</span>
								<span
									className={
										isMultiSelectOpen ? "is-open" : ""
									}
									aria-hidden="true"
								>
									⌄
								</span>
							</button>
							{isMultiSelectOpen ? (
								<div
									id={`paul-options-${spec.questionId}`}
									className={`paul-input-prompt__multi-select-menu ${
										multiSelectMenuMode === "adaptive"
											? `is-adaptive opens-${multiSelectMenuLayout.placement}`
											: ""
									}`}
								>
									<input
										type="search"
										value={optionSearch}
										onChange={(event) => {
											setOptionSearch(event.target.value);
										}}
										placeholder="Search options"
										autoFocus
									/>
									<div
										className="paul-input-prompt__multi-select-options"
										style={
											multiSelectMenuMode === "adaptive"
												? {
														maxHeight: `${multiSelectMenuLayout.optionsMaxHeight}px`,
													}
												: undefined
										}
										role="listbox"
										aria-multiselectable="true"
									>
										{filteredOptions.length > 0 ? (
											filteredOptions.map((option) => {
												const checked = selected.includes(
													option.id
												);
												const optionDisabled =
													!checked &&
													selected.length >=
														spec.maxSelections;

												return (
													<label
														key={option.id}
														role="option"
														aria-selected={checked}
													>
														<input
															type="checkbox"
															checked={checked}
															disabled={optionDisabled}
															onChange={() => {
																setValidationError(null);
																setSelected((current) =>
																	checked
																		? current.filter(
																				(id) =>
																					id !== option.id
																			)
																		: [
																				...current,
																				option.id,
																			]
																);
															}}
														/>
														<span>
															{getPaulOptionDisplayLabel(
																option,
																spec.options
															)}
														</span>
													</label>
												);
											})
										) : (
											<p>No matching options</p>
										)}
									</div>
								</div>
							) : null}
						</div>
					) : (
						<div
							className={`paul-input-prompt__options ${
								spec.options.length >=
								OPTION_SEARCH_THRESHOLD
									? "has-search"
									: ""
							}`}
						>
							{spec.options.length >=
							OPTION_SEARCH_THRESHOLD ? (
								<input
									className="paul-input-prompt__option-search"
									type="search"
									value={optionSearch}
									onChange={(event) => {
										setOptionSearch(event.target.value);
									}}
									placeholder="Search options"
									aria-label="Search options"
								/>
							) : null}
							<div className="paul-input-prompt__option-list">
								{filteredOptions.map((option) => {
									const singleSelect =
										spec.maxSelections === 1;
									const checked = selected.includes(
										option.id
									);
									return (
										<label key={option.id}>
											<input
												type={
													singleSelect
														? "radio"
														: "checkbox"
												}
												name={`paul-input-${spec.questionId}`}
												value={option.id}
												checked={checked}
												onChange={() => {
													setValidationError(null);
													setSelected((current) => {
														if (singleSelect) {
															return [option.id];
														}
														if (
															current.includes(option.id)
														) {
															return current.filter(
																(id) => id !== option.id
															);
														}
														if (
															current.length >=
															spec.maxSelections
														) {
															return current;
														}
														return [...current, option.id];
													});
												}}
											/>
											<span>
												{getPaulOptionDisplayLabel(
													option,
													spec.options
												)}
											</span>
										</label>
									);
								})}
								{filteredOptions.length === 0 ? (
									<p>No matching options</p>
								) : null}
							</div>
						</div>
					)}
				</fieldset>
			) : (
				<div className="paul-input-prompt__freeform">
					{spec.answerType === "float" ? (
						<input
							type="number"
							value={value}
							onChange={(event) => {
								setValue(event.target.value);
								setValidationError(null);
							}}
							min={spec.constraints.min ?? undefined}
							max={spec.constraints.max ?? undefined}
							step={
								spec.constraints.decimal_digits !==
								undefined
									? 10 ** -spec.constraints.decimal_digits
									: "any"
							}
							placeholder="Enter a number"
							disabled={controlsDisabled}
						/>
					) : (
						<textarea
							value={value}
							onChange={(event) => {
								setValue(event.target.value);
								setValidationError(null);
							}}
							placeholder="Type your response"
							disabled={controlsDisabled}
							rows={3}
						/>
					)}
					{spec.answerType === "float" ? (
						<small>{getNumberHint(spec.constraints)}</small>
					) : null}
				</div>
			)}

			{validationError ? (
				<div
					className="paul-input-prompt__error"
					role="alert"
				>
					{validationError}
				</div>
			) : null}

			{active && !readOnly ? (
				<button
					type="button"
					className="paul-input-prompt__submit"
					onClick={() => {
						void handleSubmit();
					}}
					disabled={controlsDisabled}
				>
					{isSubmitting ? "Submitting…" : "Submit response"}
				</button>
			) : null}
		</div>
	);
};

export default PaulInputPrompt;
